import {
  ASSET_ROUTES,
  GATEWAY_ROUTES,
  DeviceListResponseSchema,
  InviteLookupResponseSchema,
  LoginResponseSchema,
  MeResponseSchema,
  RecoveryCodesResponseSchema,
  SessionListResponseSchema,
  TotpConfirmResponseSchema,
  TotpEnrollResponseSchema,
  WORLD_ROUTES,
  fillRoute,
} from "@uzverse/contracts";
import type {
  MeResponse,
  SettingDescriptor,
  VoicePreviewResponse,
  WorldOrgResponse,
} from "@uzverse/contracts";
import { ErrorBodySchema, UzError } from "@uzverse/core";
import type { Locale, Task } from "@uzverse/core";
import { COOKIE_SESSION } from "./types.js";
import type { SessionGrant, SessionUser, WebApi } from "./types.js";

/**
 * `WebApi` over the gateway's HTTP contract (`GATEWAY_ROUTES` in `@uzverse/contracts`). The
 * session is the HttpOnly cookie the identity routes set (same origin through the dev proxy or
 * Caddy); a real bearer token is sent only when one was set explicitly.
 */
export function createHttpApi(baseUrl = ""): WebApi {
  let token: string | null = null;
  let me: MeResponse | null = null;
  const invites = new Map<string, string>();
  const R = GATEWAY_ROUTES;

  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    let response: Response;
    try {
      response = await fetch(`${baseUrl}${path}`, {
        method,
        credentials: "same-origin",
        headers: {
          ...(body === undefined ? {} : { "content-type": "application/json" }),
          ...(token && token !== COOKIE_SESSION ? { authorization: `Bearer ${token}` } : {}),
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    } catch {
      throw new UzError("unavailable", "network error");
    }
    if (response.status === 204) return undefined as T;
    const json: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const parsed = ErrorBodySchema.safeParse(json);
      if (parsed.success) {
        const { code, message, details } = parsed.data.error;
        throw new UzError(code, message, details);
      }
      throw new UzError("internal", "request failed");
    }
    return json as T;
  }

  const toUser = (m: MeResponse): SessionUser => ({
    id: m.user.id,
    email: m.user.email,
    displayName: m.user.displayName || m.user.email,
    locale: m.settings.locale ?? m.user.locale,
    isOwner: m.user.isOwner,
    ...(m.user.mustChangePassword ? { mustChangePassword: true } : {}),
  });

  async function loadMe(): Promise<SessionUser> {
    me = MeResponseSchema.parse(await request("GET", R.me));
    return toUser(me);
  }

  async function grant(): Promise<SessionGrant> {
    token = COOKIE_SESSION;
    return { token: COOKIE_SESSION, user: await loadMe() };
  }

  async function primaryCompany(): Promise<string> {
    const companies = (me ?? MeResponseSchema.parse(await request("GET", R.me))).companies;
    const first = companies[0];
    if (!first) throw new UzError("permission_denied", "no company");
    return first.id;
  }

  return {
    setToken(next) {
      token = next;
      if (!next) me = null;
    },
    async login(email, password, opts) {
      const r = LoginResponseSchema.parse(
        await request("POST", R.login, {
          email,
          password,
          ...(opts?.remember !== undefined ? { remember: opts.remember } : {}),
        }),
      );
      if (r.status === "mfa_required")
        return { status: "totp_required", challengeId: r.challengeToken };
      return { status: "ok", ...(await grant()) };
    },
    async verifyTotp(challengeId, code, opts) {
      await request("POST", R.mfa, {
        challengeToken: challengeId,
        code,
        ...(opts?.trustDevice ? { trustDevice: true } : {}),
      });
      return grant();
    },
    async listSessions() {
      return SessionListResponseSchema.parse(await request("GET", R.sessions)).sessions;
    },
    async revokeSession(id) {
      await request("DELETE", `${R.sessions}/${encodeURIComponent(id)}`);
    },
    async listDevices() {
      return DeviceListResponseSchema.parse(await request("GET", R.devices)).devices;
    },
    async revokeDevice(id) {
      await request("DELETE", `${R.devices}/${encodeURIComponent(id)}`);
    },
    async getMe() {
      try {
        return await loadMe();
      } catch (err) {
        if (
          err instanceof UzError &&
          (err.code === "unauthenticated" || err.code === "permission_denied")
        ) {
          return null; // 401 / 403: the session is gone. Anything else is transient and rethrown.
        }
        throw err;
      }
    },
    async logout() {
      await request("POST", R.logout);
      me = null;
    },
    async changePassword(currentPassword, newPassword) {
      await request("POST", R.changePassword, { currentPassword, newPassword });
      me = null;
    },
    async getInvite(inviteToken) {
      const r = InviteLookupResponseSchema.parse(
        await request("POST", R.inviteLookup, { token: inviteToken }),
      );
      invites.set(inviteToken, r.email);
      return { email: r.email, invitedByName: r.invitedByName ?? "", expiresAt: r.expiresAt };
    },
    async acceptInvite({ inviteToken, displayName, password, disclosureAccepted, locale }) {
      const resolved: Locale = locale ?? "tr";
      await request("POST", R.inviteAccept, {
        token: inviteToken,
        displayName,
        password,
        locale: resolved,
        disclosureAccepted,
      });
      // Sign in with the new password, then enroll the authenticator (always required).
      const email = invites.get(inviteToken);
      if (!email) throw new UzError("invalid_input", "invite not looked up");
      const login = LoginResponseSchema.parse(await request("POST", R.login, { email, password }));
      if (login.status !== "ok") throw new UzError("conflict", "unexpected second-factor step");
      token = COOKIE_SESSION;
      const enrollment = TotpEnrollResponseSchema.parse(await request("POST", R.totpEnroll));
      return {
        enrollmentId: COOKIE_SESSION,
        otpauthUri: enrollment.otpauthUri,
        manualKey: enrollment.secret,
      };
    },
    async confirmTotpEnrollment(_enrollmentId, code) {
      const done = TotpConfirmResponseSchema.parse(await request("POST", R.totpConfirm, { code }));
      return { ...(await grant()), recoveryCodes: done.recoveryCodes };
    },
    async beginReenrollment(proof) {
      const enrollment = TotpEnrollResponseSchema.parse(await request("POST", R.totpEnroll, proof));
      return {
        enrollmentId: COOKIE_SESSION,
        otpauthUri: enrollment.otpauthUri,
        manualKey: enrollment.secret,
      };
    },
    async regenerateRecoveryCodes(proof) {
      return RecoveryCodesResponseSchema.parse(await request("POST", R.recoveryCodes, proof))
        .recoveryCodes;
    },
    async listTasks(): Promise<Task[]> {
      const companyId = await primaryCompany();
      return (await request<{ tasks: Task[] }>("GET", fillRoute(R.tasks, { companyId }))).tasks;
    },
    getAssistantSettings: () => request("GET", R.assistant),
    saveAssistantSettings: (s) => request("PUT", R.assistant, s),
    listVoices: () => request("GET", R.voices),
    previewVoice: (providerId, voiceId) =>
      request<VoicePreviewResponse>("POST", R.voicePreview, { providerId, voiceId }),
    // The world org tree doubles as the Companies overview. TODO: the tree carries no agent
    // status or budget, so agents show as idle and the budget is empty until it does.
    listOrganization: async () => {
      const org = await request<WorldOrgResponse>("GET", WORLD_ROUTES.org);
      return org.companies.map((c) => ({
        id: c.company.id,
        name: c.company.name,
        ...(c.company.branding ? { branding: c.company.branding } : {}),
        ...(c.company.demo ? { demo: true } : {}),
        budget: null,
        buildings: c.buildings.map((b) => ({
          id: b.id,
          name: b.name,
          floors: [...b.floors]
            .sort((x, y) => x.level - y.level)
            .map((f) => ({
              id: f.id,
              name: f.name,
              level: f.level,
              ...(f.theme ? { theme: f.theme } : {}),
              agents: [
                ...(f.lead ? [{ ...f.lead, kind: "lead" as const }] : []),
                ...f.workers.map((w) => ({ ...w, kind: "worker" as const })),
              ].map((a) => ({
                id: a.id,
                name: a.name,
                kind: a.kind,
                ...(a.title ? { title: a.title } : {}),
                ...(a.roleKey ? { roleKey: a.roleKey } : {}),
                status: "idle" as const,
              })),
            })),
        })),
      }));
    },
    listCredits: () => request("GET", ASSET_ROUTES.credits),
    listMemory: () => request("GET", R.memory),
    correctMemory: (id, text) => request("PATCH", fillRoute(R.memoryItem, { id }), { text }),
    deleteMemory: (id) => request("DELETE", fillRoute(R.memoryItem, { id })),
    getModelCatalog: () => request("GET", R.models),
    listSettings: async () =>
      (await request<{ settings: SettingDescriptor[] }>("GET", R.moduleSettings)).settings,
    setSetting: async (key, value) =>
      (
        await request<{ setting: SettingDescriptor }>("PUT", fillRoute(R.moduleSetting, { key }), {
          value,
        })
      ).setting,
    resetSetting: async (key) =>
      (await request<{ setting: SettingDescriptor }>("DELETE", fillRoute(R.moduleSetting, { key })))
        .setting,
  };
}
